import React, { useState, useEffect } from 'react';
import { 
  Calendar, 
  Search, 
  Filter,
  Eye,
  Edit,
  Trash2,
  CheckCircle,
  Clock,
  XCircle,
  Loader2,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchBookings();
  }, [statusFilter, paymentFilter]);

  const fetchBookings = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      let url = `${API}/admin/bookings`;
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (paymentFilter !== 'all') params.append('payment_status', paymentFilter);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookings(response.data);
    } catch (err) {
      console.error('Error fetching bookings:', err);
      toast.error('Error al cargar las reservas');
    } finally {
      setLoading(false);
    }
  };

  const updateBookingStatus = async (bookingId, status) => {
    const token = localStorage.getItem('adminToken');
    try {
      await axios.put(
        `${API}/admin/bookings/${bookingId}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Estado actualizado');
      fetchBookings();
      setShowDetails(false);
    } catch (err) {
      toast.error('Error al actualizar');
    }
  };

  const deleteBooking = async (bookingId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta reserva?')) return;
    
    const token = localStorage.getItem('adminToken');
    try {
      await axios.delete(`${API}/admin/bookings/${bookingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Reserva eliminada');
      fetchBookings();
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-blue-500" />;
      case 'cancelled':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-yellow-500" />;
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Pendiente',
      confirmed: 'Confirmada',
      completed: 'Completada',
      cancelled: 'Cancelada'
    };
    return labels[status] || status;
  };

  const filteredBookings = bookings.filter(booking => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      booking.name.toLowerCase().includes(search) ||
      booking.email.toLowerCase().includes(search) ||
      booking.tour_name.toLowerCase().includes(search) ||
      booking.id.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-[#C2410C]" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="admin-bookings">
      <div className="mb-8">
        <h1 className="text-3xl font-display font-bold text-[#1C1917]">Reservas</h1>
        <p className="text-[#57534E]">Gestiona todas las reservas</p>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#57534E]" />
                <Input
                  placeholder="Buscar por nombre, email o ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="bookings-search"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]" data-testid="status-filter">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                <SelectItem value="pending">Pendiente</SelectItem>
                <SelectItem value="confirmed">Confirmada</SelectItem>
                <SelectItem value="completed">Completada</SelectItem>
                <SelectItem value="cancelled">Cancelada</SelectItem>
              </SelectContent>
            </Select>
            <Select value={paymentFilter} onValueChange={setPaymentFilter}>
              <SelectTrigger className="w-[180px]" data-testid="payment-filter">
                <SelectValue placeholder="Pago" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los pagos</SelectItem>
                <SelectItem value="pending">Pendiente</SelectItem>
                <SelectItem value="paid">Pagado</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Bookings Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#FDFBF7] border-b border-[#E5E5E5]">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-[#57534E]">ID</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-[#57534E]">Cliente</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-[#57534E]">Tour</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-[#57534E]">Fecha</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-[#57534E]">Total</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-[#57534E]">Estado</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-[#57534E]">Pago</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-[#57534E]">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredBookings.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-[#57534E]">
                      No se encontraron reservas
                    </td>
                  </tr>
                ) : (
                  filteredBookings.map((booking) => (
                    <tr key={booking.id} className="border-b border-[#E5E5E5] hover:bg-[#FDFBF7]">
                      <td className="px-4 py-3 text-sm font-mono text-[#57534E]">
                        {booking.id.substring(0, 8)}...
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-[#1C1917]">{booking.name}</p>
                        <p className="text-sm text-[#57534E]">{booking.email}</p>
                      </td>
                      <td className="px-4 py-3 text-sm text-[#1C1917]">{booking.tour_name}</td>
                      <td className="px-4 py-3 text-sm text-[#1C1917]">
                        {booking.date} {booking.time}
                      </td>
                      <td className="px-4 py-3 text-sm font-bold text-[#C2410C]">
                        {formatPrice(booking.total_price)}
                        {booking.discount_amount > 0 && (
                          <span className="block text-xs text-green-600">
                            -{formatPrice(booking.discount_amount)}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="flex items-center gap-1">
                          {getStatusIcon(booking.status)}
                          <span className="text-sm">{getStatusLabel(booking.status)}</span>
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`text-xs px-2 py-1 rounded-full ${
                            booking.payment_status === 'paid'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}
                        >
                          {booking.payment_status === 'paid' ? 'Pagado' : 'Pendiente'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => { setSelectedBooking(booking); setShowDetails(true); }}
                            data-testid={`view-booking-${booking.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => deleteBooking(booking.id)}
                            className="text-red-500 hover:text-red-700"
                            data-testid={`delete-booking-${booking.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Booking Details Modal */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">Detalles de la Reserva</DialogTitle>
          </DialogHeader>
          {selectedBooking && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-[#57534E]">ID de Reserva</p>
                  <p className="font-mono">{selectedBooking.id}</p>
                </div>
                <div>
                  <p className="text-sm text-[#57534E]">Estado</p>
                  <div className="flex items-center gap-2">
                    {getStatusIcon(selectedBooking.status)}
                    <span>{getStatusLabel(selectedBooking.status)}</span>
                  </div>
                </div>
              </div>

              <div className="border-t border-[#E5E5E5] pt-4">
                <h4 className="font-semibold mb-3">Información del Cliente</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-[#57534E]">Nombre</p>
                    <p className="font-medium">{selectedBooking.name}</p>
                  </div>
                  <div>
                    <p className="text-[#57534E]">Email</p>
                    <p className="font-medium">{selectedBooking.email}</p>
                  </div>
                  <div>
                    <p className="text-[#57534E]">Teléfono</p>
                    <p className="font-medium">{selectedBooking.phone}</p>
                  </div>
                  <div>
                    <p className="text-[#57534E]">Recogida</p>
                    <p className="font-medium">{selectedBooking.hotel || selectedBooking.pickup_point}</p>
                  </div>
                </div>
              </div>

              <div className="border-t border-[#E5E5E5] pt-4">
                <h4 className="font-semibold mb-3">Detalles del Tour</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-[#57534E]">Tour</p>
                    <p className="font-medium">{selectedBooking.tour_name}</p>
                  </div>
                  <div>
                    <p className="text-[#57534E]">Fecha y Hora</p>
                    <p className="font-medium">{selectedBooking.date} {selectedBooking.time}</p>
                  </div>
                  <div>
                    <p className="text-[#57534E]">Personas</p>
                    <p className="font-medium">
                      {selectedBooking.adults} adultos
                      {selectedBooking.children > 0 && `, ${selectedBooking.children} niños`}
                    </p>
                  </div>
                  <div>
                    <p className="text-[#57534E]">Idioma</p>
                    <p className="font-medium uppercase">{selectedBooking.language}</p>
                  </div>
                </div>
              </div>

              <div className="border-t border-[#E5E5E5] pt-4">
                <h4 className="font-semibold mb-3">Pago</h4>
                <div className="bg-[#FDFBF7] rounded-lg p-4">
                  <div className="flex justify-between items-center mb-2">
                    <span>Precio original</span>
                    <span>{formatPrice(selectedBooking.original_price || selectedBooking.total_price)}</span>
                  </div>
                  {selectedBooking.discount_amount > 0 && (
                    <div className="flex justify-between items-center mb-2 text-green-600">
                      <span>Descuento ({selectedBooking.discount_code})</span>
                      <span>-{formatPrice(selectedBooking.discount_amount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center font-bold text-lg border-t border-[#E5E5E5] pt-2">
                    <span>Total</span>
                    <span className="text-[#C2410C]">{formatPrice(selectedBooking.total_price)}</span>
                  </div>
                  <div className="mt-2 text-sm">
                    <span className="text-[#57534E]">Método: </span>
                    <span>{selectedBooking.payment_method === 'online' ? 'Tarjeta' : 'Efectivo'}</span>
                    <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                      selectedBooking.payment_status === 'paid'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {selectedBooking.payment_status === 'paid' ? 'Pagado' : 'Pendiente'}
                    </span>
                  </div>
                </div>
              </div>

              {selectedBooking.special_requests && (
                <div className="border-t border-[#E5E5E5] pt-4">
                  <h4 className="font-semibold mb-2">Peticiones Especiales</h4>
                  <p className="text-sm text-[#57534E]">{selectedBooking.special_requests}</p>
                </div>
              )}

              {/* Actions */}
              <div className="border-t border-[#E5E5E5] pt-4 flex gap-2">
                <Select
                  value={selectedBooking.status}
                  onValueChange={(value) => updateBookingStatus(selectedBooking.id, value)}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pendiente</SelectItem>
                    <SelectItem value="confirmed">Confirmada</SelectItem>
                    <SelectItem value="completed">Completada</SelectItem>
                    <SelectItem value="cancelled">Cancelada</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminBookings;
